import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useSidebarStore } from '@/stores/sidebar';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { NAV_OPS, NAV_MANAGE, filterForRole, type NavItem } from './nav-items';

function NavGroup({ label, items, collapsed }: { label: string; items: NavItem[]; collapsed: boolean }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <div>
      {!collapsed ? (
        <div className="px-4 pb-1.5 pt-3.5 text-[10px] font-semibold tracking-[.14em] text-white/35">{label}</div>
      ) : null}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          data-tour={`nav:${item.to}`}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 px-4 py-2.5 text-[13px] font-medium text-white/65 transition-colors hover:bg-white/5 hover:text-white',
              isActive && 'bg-sidebar-border text-accent font-semibold shadow-[inset_3px_0_0_var(--color-accent)]',
            )
          }
          title={collapsed ? t(item.labelKey) : undefined}
        >
          <item.icon size={15} strokeWidth={1.8} className="flex-none" />
          {!collapsed ? <span>{t(item.labelKey)}</span> : null}
        </NavLink>
      ))}
    </div>
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { collapsed: collapsedPref, toggle } = useSidebarStore();
  // Between `md` and `lg` the full 216px rail leaves too little room for the
  // page beside it, so it rides collapsed. The stored preference is left alone
  // and takes over again once the window is wide enough to expand.
  const narrow = useMediaQuery('(max-width: 1023px)');
  const collapsed = collapsedPref || narrow;
  const { membership } = useActiveMembership();
  const role = membership?.role;

  return (
    <aside
      className={cn(
        'hidden h-screen flex-none flex-col bg-sidebar text-white/65 transition-[width] md:flex',
        collapsed ? 'w-[72px]' : 'w-[216px]',
      )}
    >
      <div className="flex items-center gap-2.5 border-b border-sidebar-border px-4 py-4">
        <img src="/logo-mark.png" alt="" className="h-[26px] w-[26px] flex-none rounded-[22%]" />
        {!collapsed ? <span className="font-display text-base font-bold italic tracking-tight text-white">WrapForce</span> : null}
      </div>

      <nav className="flex flex-1 flex-col overflow-y-auto pb-2">
        <NavGroup label={t('nav.operations')} items={filterForRole(NAV_OPS, role)} collapsed={collapsed} />
        <NavGroup label={t('nav.manage')} items={filterForRole(NAV_MANAGE, role)} collapsed={collapsed} />
      </nav>

      {!narrow ? (
        <button
          type="button"
          onClick={toggle}
          className="mx-3 mb-1 flex items-center gap-2 px-1 py-2 text-xs font-medium text-white/40 hover:text-white"
        >
          {collapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
          {!collapsed ? t('nav.collapse') : null}
        </button>
      ) : null}

      <NavLink
        to="/workspace"
        className={({ isActive }) =>
          cn('m-3 flex items-center gap-2.5 border-t border-sidebar-border p-2 pt-4', isActive && 'bg-sidebar-border')
        }
      >
        <div className="grid h-7 w-7 flex-none place-items-center bg-accent text-xs font-bold text-ink">
          {(membership?.name ?? '??').slice(0, 2).toUpperCase()}
        </div>
        {!collapsed ? (
          <div className="min-w-0">
            <div className="truncate text-[12.5px] font-semibold text-white/90">{membership?.name ?? t('nav.loading')}</div>
            <div className="text-[11px] text-white/40">
              {membership ? `${t(`plan.${membership.plan}.label`, { ns: 'settings' })} ${t('nav.planSuffix')}` : ''}
            </div>
          </div>
        ) : null}
      </NavLink>
    </aside>
  );
}
